var _excluded = ["clearIcon", "i18n", "id", "labelId", "multiple", "name", "onChange", "onClear", "onScrollEnd", "onSearch", "options", "open", "onUpdateOpen", "optionsOrientation", "placeholder", "placement", "portalContainer", "preselectedOptions", "readOnly", "scrollEndOffset", "searchDelay", "showSearch", "value", "visibleOptions"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useState } from 'react';
import mergeRefs from 'react-merge-refs';
import useFormControlContext from '../form_utils/form_control_context/use_form_control_context';
import IconChevronDown from '../icons/icon_chevron_down';
import IconSearch from '../icons/icon_search';
import IconX from '../icons/icon_x';
import Option from './option';
import OptionDivider from './option/option_divider';
import OptionsList from './options_list';
import scrollEnded from '../utils/scroll_ended';
import setRef from '../utils/set_ref';
import useDebounceCallback from '../utils/use_debounce_callback';
import useEffect from '../utils/use_effect';
import useEventCallback from '../utils/use_event_callback';
import useKeyPressOptionsHandler from '../utils/use_key_press_options_handler';
import SelectPopover from './select_popover';
import useStyles from './use_styles';
import UtilityButton from '../shared/utility_button';
import { jsx as ___EmotionJSX } from "@emotion/core";
var OPTION_HEIGHT = 40;
var LIST_PADDING = 8;
function selectHasValue(value) {
  return value && Array.isArray(value) && value.length > 0 && value[0] !== undefined;
}
function isPrintableCharacter(str) {
  return str.length === 1 && str.match(/\S/);
}
var keysToFocusOnSearch = ['Left',
// IE Compat
'ArrowLeft', 'Right',
// IE Compat
'ArrowRight', 'Backspace'];
var groupedKeys = ['selected', 'unselected'];
var windowSSR = typeof window === 'undefined' ? {
  pageXOffset: 0,
  pageYOffset: 0,
  scrollTo: () => {}
} : window;
var SelectInput = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var {
      clearIcon,
      i18n,
      id,
      labelId,
      multiple,
      name,
      onChange,
      onClear,
      onScrollEnd,
      onSearch,
      options,
      open,
      onUpdateOpen,
      optionsOrientation,
      placeholder,
      placement,
      portalContainer,
      preselectedOptions,
      readOnly,
      scrollEndOffset,
      searchDelay,
      showSearch,
      value,
      visibleOptions
    } = _ref,
    other = _objectWithoutProperties(_ref, _excluded);
  var [anchorEl, setAnchorEl] = React.useState(null);
  var [inputValue, setInputValue] = React.useState('');
  var [updatedOrientation, setUpdatedOrientation] = React.useState(undefined);
  var [displayOptions, setDisplayOptions] = useState([]);
  var buttonRef = React.useRef(null);
  var inputRef = React.useRef(null);
  var optionsListRef = React.useRef(null);
  var searchInputRef = React.useRef(null);
  var focusedIndexRef = React.useRef(0);
  var scrollHasEnded = React.useRef(false);
  var debouncedCallback = useDebounceCallback(searchedValue => {
    onSearch === null || onSearch === void 0 || onSearch(searchedValue);
  }, searchDelay);
  var hasValue = React.useMemo(() => selectHasValue(value), [value]);
  var handleRef = mergeRefs([inputRef, ref]);
  var {
    disabled: disabledProp,
    error,
    focused,
    onBlur,
    onEmpty,
    onFilled,
    onFocus,
    theme
  } = useFormControlContext();
  var disabled = disabledProp || options.length === 0 && !showSearch || showSearch && options.length === 0 && !open;
  var placementUpwards = placement ? placement.split('-').includes('top') : false;
  var isUpwards = updatedOrientation ? updatedOrientation.includes('top') : optionsOrientation.includes('up') || placementUpwards;
  var style = useStyles({
    disabled,
    error,
    focused,
    isUpwards,
    open
  });
  var isOptionSelected = React.useCallback(optionValue => {
    return value.some(v => v !== null && v === optionValue);
  }, [value]);
  var hasAllOptionsDisabled = options.every(_ref2 => {
    var {
      disabled
    } = _ref2;
    return disabled;
  });
  var [groupedOptions, orderedOptions] = React.useMemo(() => {
    if (open) {
      var groupInit = {
        selected: [],
        unselected: []
      };
      var _groupedOptions = options.reduce((acc, item) => {
        var state = isOptionSelected(item.value) ? 'selected' : 'unselected';
        var currentAccState = acc[state];
        return _objectSpread(_objectSpread({}, acc), {}, {
          [state]: [...currentAccState, item]
        });
      }, groupInit);
      if (multiple) {
        var _orderedOptions = [..._groupedOptions.selected, ..._groupedOptions.unselected];
        return [_groupedOptions, _orderedOptions];
      }
      return [_groupedOptions, options];
    }
    return [{
      selected: [],
      unselected: []
    }, []];
  }, [isOptionSelected, multiple, open, options]);
  var optionsHeight = React.useMemo(() => {
    var optionsLength = options.length;
    var hasScroll = visibleOptions < optionsLength;
    if (hasScroll) {
      return visibleOptions * OPTION_HEIGHT + LIST_PADDING;
    }
    return undefined;
  }, [options.length, visibleOptions]);
  var updateSelect = React.useCallback(newOpen => {
    var {
      current: selectButton
    } = buttonRef;
    if (newOpen === open) return;
    if (!newOpen && inputValue !== '') {
      var _debouncedCallback$ca;
      (_debouncedCallback$ca = debouncedCallback.cancel) === null || _debouncedCallback$ca === void 0 || _debouncedCallback$ca.call(debouncedCallback);
      setInputValue('');
      onSearch === null || onSearch === void 0 || onSearch('');
    }
    if (newOpen && document.activeElement === selectButton) {
      selectButton === null || selectButton === void 0 || selectButton.blur();
    } else {
      selectButton === null || selectButton === void 0 || selectButton.focus();
    }
    onUpdateOpen(newOpen);
  }, [debouncedCallback, inputValue, onSearch, onUpdateOpen, open]);
  React.useEffect(() => {
    setDisplayOptions(displayOptions => {
      // sync newDisplayOptions with value
      var newDisplayOptions = value.map(v => {
        // Look for it in the existing display options
        var displayOption = displayOptions.find(_ref3 => {
          var {
            value: optionValue
          } = _ref3;
          return optionValue === v;
        });
        if (displayOption) {
          return displayOption;
        }

        // Look for it in our options
        var selectedOption = options.find(_ref4 => {
          var {
            value: optionValue
          } = _ref4;
          return optionValue === v;
        });
        if (selectedOption) {
          return selectedOption;
        }

        // if value isn't on options array, find value on preselected options
        return preselectedOptions.find(_ref5 => {
          var {
            value: optionValue
          } = _ref5;
          return optionValue === v;
        });
      });
      return newDisplayOptions.filter(Boolean);
    });
  }, [value, options, preselectedOptions]);
  useEffect(() => {
    if (displayOptions.length > 0) {
      onFilled === null || onFilled === void 0 || onFilled();
    } else {
      onEmpty === null || onEmpty === void 0 || onEmpty();
    }
  }, [displayOptions, onEmpty, onFilled]);
  var getTitle = React.useCallback(() => {
    if (!multiple) {
      var renderOptionTitle = () => /*#__PURE__*/React.isValidElement(displayOptions[0].icon) ? ___EmotionJSX("div", {
        css: style.titleIcon
      }, displayOptions[0].icon, ___EmotionJSX("span", {
        css: style.titleLabel
      }, displayOptions[0].label || displayOptions[0].value)) : ___EmotionJSX("span", {
        css: style.titleLabel
      }, displayOptions[0].label || displayOptions[0].value);
      return displayOptions.length === 0 ? '' : renderOptionTitle();
    }
    return displayOptions.length === 0 ? '' : ___EmotionJSX("span", {
      css: style.titleLabelEllipsis
    }, displayOptions.map(v => v.label || v.value).join(', '));
  }, [displayOptions, multiple, style]);
  var selectValue = React.useMemo(() => {
    return displayOptions.length === 0 ? '' : displayOptions.map(v => v.label || v.value).join(', ');
  }, [displayOptions]);
  var focusSearchInput = () => {
    var _searchInputRef$curre;
    (_searchInputRef$curre = searchInputRef.current) === null || _searchInputRef$curre === void 0 || _searchInputRef$curre.focus();
  };
  var selectTitle = getTitle();
  var buttonPlaceholder = placeholder && ___EmotionJSX("div", {
    className: "vn-select__placeholder",
    css: style.placeholder
  }, ___EmotionJSX("span", {
    css: style.placeholderEllipsis
  }, placeholder));
  var buttonTitle = selectTitle || buttonPlaceholder || ___EmotionJSX("span", {
    css: style.buttonTitle,
    dangerouslySetInnerHTML: {
      __html: '&#8203;'
    }
  });
  if (selectValue) {
    buttonTitle = selectTitle;
  }
  var handleBlur = React.useCallback(event => {
    onBlur === null || onBlur === void 0 || onBlur(event);
  }, [onBlur]);
  var handleFocus = React.useCallback(event => {
    onFocus === null || onFocus === void 0 || onFocus(event);
  }, [onFocus]);
  var handleClearKeyDown = React.useCallback(event => {
    if (disabled || readOnly) return;
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        onClear === null || onClear === void 0 || onClear();
        onEmpty === null || onEmpty === void 0 || onEmpty();
        break;
      default:
        break;
    }
  }, [disabled, onClear, onEmpty, readOnly]);
  var handleClearClick = React.useCallback(event => {
    if (disabled || readOnly || event.button !== 0) return;
    if (open) updateSelect(false);
    onClear === null || onClear === void 0 || onClear();
    onEmpty === null || onEmpty === void 0 || onEmpty();
  }, [disabled, onClear, onEmpty, open, readOnly, updateSelect]);
  var handleMouseDown = React.useCallback(event => {
    if (disabled || readOnly || event.button !== 0) return;
    event.preventDefault();
    updateSelect(!open);
  }, [disabled, open, readOnly, updateSelect]);
  var getValidOptionIndex = React.useCallback((index, direction) => {
    if (!optionsListRef.current) return 0;
    var nextIndex = index;
    while (true) {
      var _optionsListRef$curre;
      var option = (_optionsListRef$curre = optionsListRef.current) === null || _optionsListRef$curre === void 0 ? void 0 : _optionsListRef$curre.querySelector("[data-option-index=\"".concat(nextIndex, "\"]"));
      var nextOptionDisabled = option && option.getAttribute('aria-disabled') === 'true';
      if (nextOptionDisabled) {
        nextIndex += direction === 'next' ? 1 : -1;
        if (direction === 'next' && nextIndex === orderedOptions.length) nextIndex = 0;
        if (direction === 'previous' && nextIndex === -1) nextIndex = orderedOptions.length - 1;
      } else {
        if (direction === 'next' && nextIndex === orderedOptions.length) return 0;
        if (direction === 'previous' && nextIndex === -1) return orderedOptions.length - 1;
        return index !== -1 ? nextIndex : 0;
      }
    }
  }, [orderedOptions.length]);
  var setFocusedIndex = useEventCallback(_ref6 => {
    var {
      index,
      trigger = 'auto'
    } = _ref6;
    focusedIndexRef.current = index;
    if (!optionsListRef.current) return;
    var {
      current: optionsList
    } = optionsListRef;
    if (!open) {
      optionsList.removeAttribute('aria-activedescendant');
    } else {
      optionsList.setAttribute('aria-activedescendant', "".concat(id, "-option-").concat(index));
    }
    var previousFocus = optionsList.querySelector('[data-focus]');
    if (previousFocus) {
      previousFocus.removeAttribute('data-focus');
    }
    if (index === -1) {
      optionsList.scrollTop = 0;
      return;
    }
    var option = optionsList.querySelector("[data-option-index=\"".concat(index, "\"]"));
    if (!option) return;
    option.setAttribute('data-focus', 'true');
    if (optionsList.scrollHeight > optionsList.clientHeight && trigger !== 'hover') {
      var element = option;
      var scrollBottom = optionsList.clientHeight + optionsList.scrollTop;
      var elementBottom = element.offsetTop + element.offsetHeight;
      if (elementBottom > scrollBottom) {
        optionsList.scrollTop = elementBottom - optionsList.clientHeight;
      } else if (element.offsetTop < optionsList.scrollTop) {
        optionsList.scrollTop = element.offsetTop;
      }
    }
  });
  var syncFocusedIndex = React.useCallback(() => {
    if (scrollHasEnded.current) {
      scrollHasEnded.current = false;
      return;
    }
    var recentValue = value[0];
    if (!optionsListRef.current) return;
    var selectedItemIndex = recentValue != null && !hasAllOptionsDisabled ? orderedOptions.findIndex(_ref7 => {
      var {
        value: optionValue
      } = _ref7;
      return isOptionSelected(optionValue);
    }) : -1;
    var indexToFocus = getValidOptionIndex(selectedItemIndex, 'next');
    var {
      current: focusedIndex
    } = focusedIndexRef;
    if (!showSearch && focusedIndex) {
      indexToFocus = getValidOptionIndex(focusedIndex, 'next');
    }
    if (indexToFocus === 0) {
      setFocusedIndex({
        index: indexToFocus - 1
      });
    } else {
      setFocusedIndex({
        index: indexToFocus
      });
    }
  }, [orderedOptions, getValidOptionIndex, hasAllOptionsDisabled, isOptionSelected, setFocusedIndex, showSearch, value]);
  var keyPressOptionsHandlerCallback = newHighlightedIndex => {
    setFocusedIndex({
      index: newHighlightedIndex,
      trigger: 'keyboard'
    });
  };
  var handleKeyPress = useKeyPressOptionsHandler(orderedOptions, keyPressOptionsHandlerCallback, 500);
  var handleOptionsListRef = useEventCallback(node => {
    setRef(optionsListRef, node);
    var {
      pageXOffset,
      pageYOffset
    } = windowSSR;
    if (!node) return;
    syncFocusedIndex();
    if (showSearch) {
      focusSearchInput();
    } else {
      node.focus();
    }
    windowSSR.scrollTo(pageXOffset, pageYOffset);
  });
  React.useEffect(() => {
    if (open) syncFocusedIndex();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  var focusOptionSelection = React.useCallback(key => {
    var nextIndex = key === 'End' ? options.length - 1 : 0;
    var direction = key === 'End' ? 'previous' : 'next';
    var validIndex = getValidOptionIndex(nextIndex, direction);
    setFocusedIndex({
      index: validIndex,
      trigger: 'keyboard'
    });
  }, [getValidOptionIndex, options.length, setFocusedIndex]);
  var nextOptionSelection = React.useCallback(key => {
    var diff = key === 'Down' /* IE Compat */ || key === 'ArrowDown' ? 1 : -1;
    var direction = key === 'Down' /* IE Compat */ || key === 'ArrowDown' ? 'next' : 'previous';
    var nextIndex = focusedIndexRef.current + diff;
    var validIndex = getValidOptionIndex(nextIndex, direction);
    setFocusedIndex({
      index: validIndex,
      trigger: 'keyboard'
    });
  }, [getValidOptionIndex, setFocusedIndex]);
  var handleOptionClick = React.useCallback(event => {
    if (event.button !== 0 || event.currentTarget.getAttribute('aria-disabled') === 'true') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    var index = Number(event.currentTarget.getAttribute('data-option-index'));
    var option = orderedOptions[index];
    onChange(option);
    if (!multiple) updateSelect(false);
  }, [orderedOptions, multiple, onChange, updateSelect]);
  var handleScroll = React.useCallback(event => {
    if (onScrollEnd && scrollEnded(event.target, scrollEndOffset)) {
      scrollHasEnded.current = true;
      onScrollEnd();
    }
  }, [onScrollEnd, scrollEndOffset]);
  var handleSearch = React.useCallback(event => {
    var newValue = event.target.value;
    if (newValue !== inputValue) {
      setInputValue(newValue);
      debouncedCallback(newValue);
    }
  }, [debouncedCallback, inputValue]);
  var handleOptionListMouseDown = React.useCallback(event => event.preventDefault(), []);
  var handleOptionListKeyDown = React.useCallback(event => {
    var {
      key,
      shiftKey
    } = event;
    switch (key) {
      case 'Tab':
        event.preventDefault();
        if (shiftKey) {
          focusSearchInput();
        } else {
          updateSelect(false);
        }
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        updateSelect(false);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (focusedIndexRef.current !== -1) {
          var option = orderedOptions[focusedIndexRef.current];
          if (!option.disabled) {
            onChange(option);
            if (!multiple) updateSelect(false);
          }
        }
        break;
      case 'End':
      case 'Home':
        event.preventDefault();
        if (!hasAllOptionsDisabled) focusOptionSelection(key);
        break;
      case 'Up': // IE Compat
      case 'ArrowUp':
        event.preventDefault();
        if (!hasAllOptionsDisabled) {
          nextOptionSelection(key);
        } else if (open) {
          event.stopPropagation();
          updateSelect(false);
        }
        break;
      case 'Down': // IE Compat
      case 'ArrowDown':
        event.preventDefault();
        if (!hasAllOptionsDisabled) nextOptionSelection(key);
        break;
      default:
        if (showSearch) {
          var isKeyToFocusOnSearch = keysToFocusOnSearch.includes(key);
          var hasKeyToHandle = isPrintableCharacter(key) || isKeyToFocusOnSearch;
          if (hasKeyToHandle) {
            event.persist();
            event.stopPropagation();
            focusSearchInput();
          }
        } else if (isPrintableCharacter(key)) {
          event.persist();
          event.stopPropagation();
          handleKeyPress(key);
        }
        break;
    }
  }, [orderedOptions, focusOptionSelection, handleKeyPress, hasAllOptionsDisabled, multiple, nextOptionSelection, onChange, open, showSearch, updateSelect]);
  var handleSearchInputKeydown = React.useCallback(event => {
    var {
      key,
      shiftKey
    } = event;
    switch (key) {
      case 'Tab':
        event.preventDefault();
        if (shiftKey || !optionsListRef.current) {
          updateSelect(false);
        } else {
          optionsListRef.current.focus();
        }
        break;
      case 'Enter':
        event.preventDefault();
        handleOptionListKeyDown(event);
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        updateSelect(false);
        break;
      case 'ArrowUp':
      case 'ArrowDown':
        event.preventDefault();
        if (optionsListRef.current) {
          optionsListRef.current.focus();
          handleOptionListKeyDown(event);
        }
        break;
      default:
        break;
    }
  }, [handleOptionListKeyDown, updateSelect]);
  var handleKeyDown = React.useCallback(event => {
    if (disabled || readOnly) return;
    var {
      key
    } = event;
    switch (key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        updateSelect(!open);
        break;
      case 'Escape':
        updateSelect(false);
        break;
      case 'End':
      case 'Home':
      case 'Up': // IE Compat
      case 'ArrowUp':
      case 'Down': // IE Compat
      case 'ArrowDown':
        event.preventDefault();
        updateSelect(true);
        break;
      default:
        if (isPrintableCharacter(key)) {
          if (!open) updateSelect(true);
          handleOptionListKeyDown(event);
        }
        break;
    }
  }, [disabled, handleOptionListKeyDown, open, readOnly, updateSelect]);
  var clearButton = !readOnly && !disabled && clearIcon && hasValue && ___EmotionJSX(UtilityButton, {
    "aria-label": i18n.clear,
    className: "vn-select__clear-button",
    css: style.clearButton,
    onKeyDown: handleClearKeyDown,
    onClick: handleClearClick,
    icon: ___EmotionJSX(IconX, {
      "aria-hidden": true
    }),
    title: i18n.clear
  });
  var openButton = ___EmotionJSX("div", {
    "aria-label": i18n.open,
    className: "vn-select__open-button",
    css: style.openButton,
    onBlur: handleBlur,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    onMouseDown: handleMouseDown,
    role: "button",
    tabIndex: disabled ? -1 : 0,
    title: i18n.open
  }, ___EmotionJSX(IconChevronDown, {
    "aria-hidden": true
  }));
  var renderOption = React.useCallback((option, index) => {
    var {
      disabled: optionDisabled,
      icon,
      label,
      value: optionValue
    } = option;
    return ___EmotionJSX(Option, {
      "data-option-index": index,
      "data-value": optionValue,
      disabled: optionDisabled || false,
      icon: icon,
      id: id ? "".concat(id, "-option-").concat(index) : undefined,
      key: optionValue === null || optionValue === void 0 ? void 0 : optionValue.toString(),
      multiple: multiple,
      onClick: handleOptionClick,
      selected: isOptionSelected(optionValue),
      tabIndex: -1
    }, label || optionValue);
  }, [handleOptionClick, id, isOptionSelected, multiple]);
  var renderOptions = () => {
    var renderedOptions = [];
    var isFiltered = showSearch && inputValue !== '';
    var {
      length: optionsLength
    } = options;
    if (isFiltered && optionsLength > 0) {
      var i18nLabel = optionsLength > 1 ? i18n.showingResults : i18n.showingResult;
      var showingItemsLabel = i18nLabel.replace(/(%s)/g, optionsLength.toString());
      renderedOptions.push(___EmotionJSX(OptionDivider, {
        customStyle: style.showingResultsDivider,
        key: "divider-showing-items"
      }, showingItemsLabel));
    }
    if (multiple) {
      var i = 0;
      groupedKeys.forEach(dividerLabel => {
        if (!isFiltered && hasValue) {
          // Renders the divider label for selected and unselected options
          renderedOptions.push(___EmotionJSX(OptionDivider, {
            key: "divider-".concat(dividerLabel)
          }, i18n[dividerLabel]));
        }
        groupedOptions[dividerLabel].forEach(op => {
          renderedOptions.push(renderOption(op, i));
          i += 1;
        });
      });
    } else {
      renderedOptions.push(options.map((option, index) => renderOption(option, index)));
    }
    return renderedOptions;
  };
  var optionListId = id ? "".concat(id, "-listbox") : undefined;
  var handleBackdropClick = React.useCallback(() => updateSelect(false), [updateSelect]);
  return ___EmotionJSX(React.Fragment, null, ___EmotionJSX("div", _extends({
    css: style.root,
    ref: setAnchorEl
  }, other), ___EmotionJSX("div", {
    "aria-controls": optionListId,
    "aria-disabled": disabled,
    "aria-expanded": open ? true : undefined,
    "aria-haspopup": "listbox",
    "aria-labelledby": [labelId, id].filter(Boolean).join(' ') || undefined,
    className: "label",
    css: style.button,
    id: id,
    onBlur: handleBlur,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    onMouseDown: handleMouseDown,
    ref: buttonRef,
    role: "combobox",
    tabIndex: disabled ? -1 : 0
  }, buttonTitle), ___EmotionJSX("input", {
    name: name,
    ref: handleRef,
    type: "hidden",
    value: selectValue
  }), ___EmotionJSX("span", {
    css: style.buttons
  }, clearButton, openButton)), open && anchorEl && ___EmotionJSX(SelectPopover, {
    anchorEl: anchorEl,
    onBackdropClick: handleBackdropClick,
    onChangePlacement: setUpdatedOrientation,
    optionsOrientation: optionsOrientation,
    placement: placement,
    portalContainer: portalContainer,
    zIndex: theme.elevation.popover
  }, ___EmotionJSX("div", {
    css: style.content
  }, showSearch ? ___EmotionJSX("div", {
    css: style.searchDiv
  }, ___EmotionJSX("input", {
    autoComplete: "off",
    css: style.searchInput,
    onChange: handleSearch,
    onKeyDown: handleSearchInputKeydown,
    placeholder: i18n.searchPlaceholder,
    ref: searchInputRef,
    tabIndex: -1,
    value: inputValue
  }), ___EmotionJSX(IconSearch, {
    "aria-hidden": true,
    css: style.searchIcon
  })) : null, showSearch && options.length === 0 ? ___EmotionJSX("div", {
    css: style.noResults
  }, i18n.noResults) : null, options.length > 0 ? ___EmotionJSX(OptionsList, {
    "aria-activedescendant": open ? '' : undefined,
    "aria-labelledby": labelId,
    "aria-multiselectable": multiple ? true : undefined,
    id: optionListId,
    onKeyDown: handleOptionListKeyDown,
    onMouseDown: handleOptionListMouseDown,
    onScroll: handleScroll,
    optionsHeight: optionsHeight,
    ref: handleOptionsListRef
  }, renderOptions()) : null)));
});
SelectInput.displayName = 'SelectInput';
export default SelectInput;